/**
 * Product content enrichment — "enter once → enrich → approve".
 *
 * Turns the commercial facts an operator has already typed (name, brand,
 * category, product type, option names/values) into a *draft* description,
 * short description, SEO title, meta description and tags. The draft is never
 * applied automatically: the create flow surfaces it for explicit review and
 * the operator approves it by applying/editing it before the product exists.
 *
 * The single server-side entry point is the `ai-orchestrator` (task
 * `product.enrich_content`). It prefers an external model when one is
 * configured and budget permits, and otherwise returns a deterministic
 * templated draft. This module only:
 *   1. normalises the divergent result shapes into one canonical draft, and
 *   2. provides the same deterministic builder as a local fallback so the
 *      experience is never a dead end and never invents a specification.
 */

import { runAiTask, type AiTaskResult } from "@/lib/ai";
import {
  PRODUCT_ENRICH_TASK,
  buildNeutralProductCopy,
  marketingDraftHasContent,
  pickMarketingDraft,
  toProductEnrichParams,
  type ExistingProductCopy,
  type ProductEnrichFacts,
} from "@/lib/product-ai-boundary";

export type EnrichmentFacts = ProductEnrichFacts & { optionGroups?: Record<string, string[]> };

/** Why a deterministic draft was shown instead of a fresh external AI draft. */
export type EnrichmentFallbackReason =
  | "ai_disabled"
  | "budget_blocked"
  | "not_configured"
  | "invocation_failed"
  | "provider_failed"
  | "grounding_rejected"
  | "orchestrator_deterministic";

export interface EnrichmentDraft {
  description: string;
  shortDescription: string;
  seoTitle: string;
  metaDescription: string;
  tags: string[];
  source: "external" | "deterministic" | "cache";
  fallbackReason?: EnrichmentFallbackReason;
  seoKeywords?: string;
  urlSlug?: string;
}

/**
 * Operator-facing caption for a draft. Never claims "no external AI configured"
 * unless that is the actual orchestrator outcome.
 */
export function enrichmentDraftCaption(draft: EnrichmentDraft): string {
  if (draft.source === "external") return "AI draft — review before applying";
  if (draft.source === "cache") return "AI draft (cached) — review before applying";
  switch (draft.fallbackReason) {
    case "ai_disabled":
      return "External AI is disabled for this organisation — showing internal fallback draft.";
    case "budget_blocked":
      return "AI budget limit reached — showing internal fallback draft.";
    case "not_configured":
      return "No external AI configured — showing internal fallback draft.";
    case "invocation_failed":
    case "provider_failed":
      return "External AI request failed — showing internal fallback draft.";
    case "grounding_rejected":
      return "External AI draft failed grounding checks — showing internal fallback draft.";
    case "orchestrator_deterministic":
    default:
      return "Internal fallback draft — review before applying";
  }
}

/**
 * Classify why the orchestrator returned a deterministic (or empty) result.
 * Uses only non-sensitive top-level flags from ai-orchestrator.
 */
export function classifyEnrichmentFallback(res: AiTaskResult | null | undefined): EnrichmentFallbackReason {
  if (!res) return "invocation_failed";
  if (res.ai_disabled === true) return "ai_disabled";
  if (res.budget_blocked === true) return "budget_blocked";
  if (res.fallback_error) return "provider_failed";
  if (res.external_isolated === true || res.provider_key_present === false) return "not_configured";
  if (res.deterministic) return "orchestrator_deterministic";
  return "orchestrator_deterministic";
}

/**
 * Normalise a result payload (external model JSON, deterministic object, or a
 * generic/disabled message) into a canonical draft. Returns `null` when the
 * payload does not contain any usable draft field — the caller then falls back
 * to `buildDeterministicDraft`.
 */
function resultWasComputedInternally(result: unknown): boolean {
  return !!result
    && typeof result === "object"
    && (result as Record<string, unknown>).computed_internally === true;
}

export function parseEnrichmentResult(
  result: unknown,
  source: EnrichmentDraft["source"],
  fallbackReason?: EnrichmentFallbackReason,
): EnrichmentDraft | null {
  const picked = pickMarketingDraft(result);
  if (!marketingDraftHasContent(picked)) return null;

  return {
    description: picked.description || picked.short_description || picked.seo_title,
    shortDescription: picked.short_description,
    seoTitle: picked.seo_title,
    metaDescription: picked.meta_description,
    tags: picked.tags,
    seoKeywords: picked.seo_keywords,
    source,
    ...(source === "deterministic" && fallbackReason ? { fallbackReason } : {}),
  };
}

/**
 * Pure, deterministic draft builder. Composes only from facts already entered —
 * it never invents materials, dimensions, certifications or any specification
 * the operator has not supplied. Used as the local fallback and exercised
 * directly in unit tests.
 */
function applyLocalGroupedSeo(
  draft: EnrichmentDraft,
  facts: EnrichmentFacts,
): EnrichmentDraft {
  if (!facts.optionGroups || Object.keys(facts.optionGroups).length === 0) return draft;
  const neutral = buildNeutralProductCopy({
    name: facts.name,
    brand: facts.brand,
    category: facts.categoryName,
    options: facts.optionNames,
    values: facts.optionValues,
    optionGroups: facts.optionGroups,
  });
  return {
    ...draft,
    // External AI receives the approved flat option name/value boundary only.
    // Canonical meta is rebuilt locally where the browser still knows which
    // values belong to Colour, Size, etc.
    metaDescription: neutral.meta_description,
  };
}

export function buildDeterministicDraft(
  facts: EnrichmentFacts,
  fallbackReason: EnrichmentFallbackReason = "orchestrator_deterministic",
): EnrichmentDraft {
  const neutral = buildNeutralProductCopy({
    name: facts.name,
    brand: facts.brand,
    category: facts.categoryName,
    options: facts.optionNames,
    values: facts.optionValues,
    optionGroups: facts.optionGroups,
  });

  return {
    description: neutral.description,
    shortDescription: neutral.short_description,
    seoTitle: neutral.seo_title,
    metaDescription: neutral.meta_description,
    tags: neutral.tags,
    seoKeywords: neutral.seo_keywords,
    source: "deterministic",
    fallbackReason,
  };
}

/**
 * Run the enrichment task and return a canonical draft. The orchestrator result
 * is parsed; if it contains no usable draft (e.g. AI disabled, no provider), a
 * deterministic draft is built locally so the operator always has something to
 * review and approve. Invocation failures keep a safe fallback but are labelled
 * as request failures — never as "no external AI configured".
 */
export async function enrichProduct(
  facts: EnrichmentFacts,
  orgId: string,
  options?: { rewrite?: boolean; existingCopy?: ExistingProductCopy },
): Promise<EnrichmentDraft> {
  try {
    const res = await runAiTask(
      PRODUCT_ENRICH_TASK,
      toProductEnrichParams({
        facts,
        rewrite: options?.rewrite === true,
        existingCopy: options?.existingCopy,
      }),
      orgId,
    );

    if (res && res.deterministic !== true && resultWasComputedInternally(res.result)) {
      return buildDeterministicDraft(facts, "grounding_rejected");
    }

    if (res?.provider === "cache" && res.deterministic !== true) {
      const cached = parseEnrichmentResult(res.result, "cache");
      if (cached) return applyLocalGroupedSeo(cached, facts);
    }

    if (res && res.deterministic !== true) {
      const external = parseEnrichmentResult(res.result, "external");
      if (external) return applyLocalGroupedSeo(external, facts);
    }

    const reason = classifyEnrichmentFallback(res);
    // Deterministic server responses only carry the external-safe flattened
    // option list. Rebuild locally so customer-facing fallback copy can use
    // grouped variant facts without expanding the external AI privacy boundary.
    return buildDeterministicDraft(facts, reason);
  } catch {
    return buildDeterministicDraft(facts, "invocation_failed");
  }
}

export function enrichableFieldsFromDraft(draft: EnrichmentDraft) {
  return {
    description: draft.description,
    shortDescription: draft.shortDescription,
    seoTitle: draft.seoTitle,
    metaDescription: draft.metaDescription,
    seoKeywords: draft.seoKeywords ?? draft.tags.join(", "),
  };
}

export interface OperatorSeoFields {
  seoTitle: string;
  metaDescription: string;
  seoKeywords: string;
  urlSlug: string;
}

/** Apply a generated SEO draft only into empty operator fields unless overwrite is explicit. */
export function mergeSeoDraft(
  current: OperatorSeoFields,
  draft: Pick<EnrichmentDraft, "seoTitle" | "metaDescription"> & { seoKeywords?: string; urlSlug?: string },
  overwriteOperatorEdits = false,
): OperatorSeoFields {
  const take = (cur: string, next?: string) => {
    if (overwriteOperatorEdits) return (next ?? "").trim();
    if (cur.trim()) return cur;
    return (next ?? "").trim();
  };
  return {
    seoTitle: take(current.seoTitle, draft.seoTitle),
    metaDescription: take(current.metaDescription, draft.metaDescription),
    seoKeywords: take(current.seoKeywords, draft.seoKeywords),
    urlSlug: take(current.urlSlug, draft.urlSlug),
  };
}
